import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig } from 'axios';
import { RootState } from '../store';
import { APIRequestStatus } from 'common-ts/dist/models/APIRequestStatus';
import jwt_decode from 'jwt-decode';
import { assignOrderItem, postUserPersonalSizeInput, uploadLocalUnsavedMeasAndSynchronize } from './MeasurementsRedux';
import { fetchOrdersList } from './OrdersListRedux';
import { fetchAddresses } from './AddressRedux';
import { useDispatch } from 'react-redux';
import { sendWebsiteEventMessage } from '../telegrambotevents';
import { UserPersonalSizeInput } from 'common-ts/dist/models/UserMeasurements';
import { removeCurrentCoupon, submitCouponRequest } from './CartRedux';
import { Mixpanel } from '../mixpanel';
import { TailorChatEvent } from '../App';
import { setSenderObject } from './LivestreamRedux';
import { terbebasClient } from '../terbebasApi';
import { getActivePixelId } from '../analytics';
import { isValidIndonesianMobile, normalizeIndonesianPhone } from 'common-ts/dist/models/Phone';

export interface AccountState {
  authState: 'signedin' | 'signedout' | 'starting',

  email: string,
  emailVerified: 'true' | 'false' | 'unknown',
  phoneNumber: string,
  phoneNumberVerified: 'true' | 'false' | 'unknown',
  name: string,
  sub: string,
  paidOrderCount: number,
  firstOrderTime: string,

  chatUserId: string,
  chatEvents: TailorChatEvent[],

  idToken: string | undefined,
  tokenExpiration: string | undefined,

  statusSignIn: APIRequestStatus,
  signInError: string,
  statusSignUp: APIRequestStatus,
  signUpError: string,

  distinctUserId: string,
}

let initialState: AccountState = {
  authState: 'starting',
  email: '',
  emailVerified: 'unknown',
  phoneNumber: '',
  phoneNumberVerified: 'unknown',
  name: '',
  sub: '',
  paidOrderCount: 0,
  firstOrderTime: '',
  chatUserId: '',
  chatEvents: [],
  idToken: undefined,
  tokenExpiration: undefined,
  statusSignIn: APIRequestStatus.Idle,
  signInError: '',
  statusSignUp: APIRequestStatus.Idle,
  signUpError: '',

  distinctUserId: localStorage.getItem('distinctUserId') ? localStorage.getItem('distinctUserId') as string : '',
}

// Legacy tailor-chat history (fareast) — kept as a no-op so livestream chat
// code paths still dispatch it harmlessly.
export const getChatHistory = createAsyncThunk(
  'account/getChatHistory',
  async (payload: { chatUserId: string }) => {
    return { content: [] };
})

// Registers a shopper account on this store (terbebas customer auth),
// then signs in. Used by checkout's optional "buat akun" path.
export const signUpAndCheckout = createAsyncThunk(
  'account/signUpAndCheckout',
  async (payload: {
    password: string,
    addressObject: any,
    onSuccessCallback: (idToken: string) => void,
    onFailureCallback: () => void
  }, thunkAPI) => {
  const rootState = (thunkAPI.getState() as RootState);

  try {
    const response = await terbebasClient.post('/storefront/auth/signup', {
      email: rootState.account.email,
      phone: rootState.account.phoneNumber,
      name: rootState.account.name,
      password: payload.password,
    });
    payload.onSuccessCallback(response.data.token + '');
    thunkAPI.dispatch(signIn({ password: payload.password, useEmail: true, otpValue: '' }));
    return response.data;
  } catch (error: any) {
    // The callback runs the address/courier steps too — surface its
    // failures instead of hiding them behind "Pendaftaran gagal".
    console.error('signUpAndCheckout failed:', error);
    thunkAPI.dispatch(updateState({
      signUpError: error?.response?.data?.error ?? 'Pendaftaran gagal',
      statusSignUp: APIRequestStatus.Idle,
    }));
    payload.onFailureCallback();
    return null;
  }
})

export const signUp = createAsyncThunk(
  'account/signUp',
  async (payload: {
    password: string
  }, thunkAPI) => {
  const rootState = (thunkAPI.getState() as RootState);

  try {
    const response = await terbebasClient.post('/storefront/auth/signup', {
      email: rootState.account.email,
      phone: rootState.account.phoneNumber,
      name: rootState.account.name,
      password: payload.password,
    });
    thunkAPI.dispatch(signIn({ password: payload.password, useEmail: true, otpValue: '' }));
    return response.data;
  } catch (error: any) {
    thunkAPI.dispatch(updateState({
      signUpError: error?.response?.data?.error ?? 'Pendaftaran gagal',
      statusSignUp: APIRequestStatus.Idle,
    }));
    return null;
  }
})

export const signIn = createAsyncThunk(
  'account/signIn',
  async (payload: {
    password: string
    useEmail: boolean
    otpValue: string
    subTempPass?: string
    userSub?: string
    orderItemId?: string
  }, thunkAPI) => {
  const rootState = (thunkAPI.getState() as RootState);

  try {
    const response = await terbebasClient.post('/storefront/auth/login', {
      email: rootState.account.email,
      password: payload.password,
    });

    const token: string = response.data.token + '';
    const customer = response.data.customer;
    const decodedToken: any = jwt_decode(token);

    thunkAPI.dispatch(updateState({
      email: customer.email ?? '',
      emailVerified: 'true',
      // Backend login now returns the account phone; fall back to whatever
      // is in state (the /auth/me hydration below fills it either way).
      phoneNumber: customer.phone ?? rootState.account.phoneNumber ?? '',
      phoneNumberVerified: 'unknown',
      name: customer.name ?? '',
      sub: customer.id,
      paidOrderCount: 0,
      firstOrderTime: '',
      tokenExpiration: decodedToken.exp + '',
      idToken: token,
      authState: 'signedin',
    }));

    (window as any).studio_s_customer_name = customer.name;
    thunkAPI.dispatch(setSenderObject({
      customerName: customer.name,
    }));

    thunkAPI.dispatch(fetchOrdersList());
    thunkAPI.dispatch(fetchAddresses());

    // Re-validate any applied coupon now that we have an account: a per-account
    // limit the shopper already hit is dropped with a reason (the request now
    // carries the token). Reads couponKey from the freshly-updated store.
    const storedCouponKey = (thunkAPI.getState() as RootState).cart.couponKey;
    if (storedCouponKey) {
      thunkAPI.dispatch(submitCouponRequest(storedCouponKey));
    }

    Mixpanel.identify(customer.id);
    Mixpanel.track('signInAttemptSuccess', { "userSub": customer.id });

    return { userLogInStatus: 'SUCCESS', data: { token } };
  } catch (error: any) {
    thunkAPI.dispatch(updateState({
      signInError: error?.response?.data?.error ?? 'Email atau password salah',
    }));
    return { userLogInStatus: 'FAILED' };
  }
})

export const signOutAndReset = createAsyncThunk(
  'account/signOutAndReset', 
  async () => {
  
  // Facebook External ID Tracking: clear the advanced-matching data so the
  // next visitor on this device isn't attributed to the signed-out account.
  // The pixel id is this tenant's own (analytics.ts) — never a hardcoded one,
  // which on a multi-tenant build would report every store to one ad account.
  const pixelId = getActivePixelId();
  if (pixelId && (window as any).fbq !== null) {
    const newUserData =  {
      "external_id": "",
      "ph": "",
      "em": ""
    };
    console.log("Sign out, linking pixel to external id " + JSON.stringify(newUserData));
    (window as any).fbq('init', pixelId, newUserData);
  }
    
  Mixpanel.reset();

  return null;
})

function combineChatEventsList(previousEventList: TailorChatEvent[], newEvent: TailorChatEvent): TailorChatEvent[] {
  const alreadyExistDict: {[key: string]: boolean} = {};

  for (let prevEvent of previousEventList) {
    alreadyExistDict[prevEvent.creationDateTime] = true;
  }

  const newCreationDateTime = newEvent.creationDateTime;

  if (alreadyExistDict[newCreationDateTime]) {
    return [...previousEventList];
  } else {
    return [...previousEventList, newEvent];
  }
}

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    restartLoginStatus: (state) => {
      state.authState = 'starting';
    },
    updateState: (state, action) => {
      const payload = action.payload;
      Object.assign(state, payload);
  
      localStorage.setItem("account", JSON.stringify(state));
    },
    newChatMessage: (state, action) => {
      const payload = action.payload;
      Object.assign(state, {
        chatEvents: combineChatEventsList(state.chatEvents, payload),
      });
    },
  },
  extraReducers(builder) {
    builder
      // /storefront/auth/me (fetched on login and on session restore) is the
      // only response carrying the account phone. Returning buyers used to
      // keep an empty phone forever — persisted in localStorage — which the
      // checkout turned into orders with phone "62". Only a valid mobile
      // overwrites, so a typed-but-unsaved phone is never clobbered by null.
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        const phone = normalizeIndonesianPhone((action.payload as { phone?: string | null }).phone);
        if (isValidIndonesianMobile(phone) && state.phoneNumber !== phone) {
          state.phoneNumber = phone;
          localStorage.setItem("account", JSON.stringify(state));
        }
      })
      .addCase(signOutAndReset.pending, (state, action) => {
      })
      .addCase(signOutAndReset.fulfilled, (state, action) => {
        state.authState = 'signedout';
        state.email = '';
        state.emailVerified = 'unknown';
        state.phoneNumber = '';
        state.phoneNumberVerified = 'unknown';
        state.name = '';
        state.sub = '';
        state.paidOrderCount = 0;
        state.firstOrderTime = '';
        state.chatUserId = 'USER-' + Math.floor(Math.random() * 100000);
        state.chatEvents = [];
        state.idToken = undefined;
        state.tokenExpiration = undefined;
        state.statusSignIn = APIRequestStatus.Idle;
        state.signInError = '';
        state.statusSignUp = APIRequestStatus.Idle;
        state.signUpError = '';
        localStorage.setItem("account", '');
      })
      .addCase(signOutAndReset.rejected, (state, action) => {
      })
      .addCase(signIn.pending, (state, action) => {
        state.statusSignIn = APIRequestStatus.RequestInProgress;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.statusSignUp = APIRequestStatus.Success; // In case sign up is executed before sign in
        state.statusSignIn = APIRequestStatus.Success;
      })
      .addCase(signIn.rejected, (state, action) => {
        console.log("fetch is failure");
        state.statusSignIn = APIRequestStatus.Failure;
      })
      .addCase(signUpAndCheckout.pending, (state, action) => {
        state.statusSignUp = APIRequestStatus.RequestInProgress;
      })
      .addCase(signUpAndCheckout.fulfilled, (state, action) => {
        // We still consider this to be in progress, since we sign in right after sign up
      })
      .addCase(signUpAndCheckout.rejected, (state, action) => {
        console.log("fetch is failure");
        state.statusSignUp = APIRequestStatus.Failure;
      })
      .addCase(signUp.pending, (state, action) => {
        state.statusSignUp = APIRequestStatus.RequestInProgress;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        // We still consider this to be in progress, since we sign in right after sign up
      })
      .addCase(signUp.rejected, (state, action) => {
        console.log("fetch is failure");
        state.statusSignUp = APIRequestStatus.Failure;
      })
      .addCase(getChatHistory.pending, (state, action) => {
      })
      .addCase(getChatHistory.fulfilled, (state, action) => {
        if (action.payload?.content) {
          state.chatEvents = action.payload.content.slice().reverse();
        }
      })
      .addCase(getChatHistory.rejected, (state, action) => {
      })
  }
})

// Action creators are generated for each case reducer function
export const { updateState, newChatMessage, restartLoginStatus } = accountSlice.actions;

export default accountSlice.reducer;